import { z } from "zod";
import { phoneNumberValidator } from "@/utils/validators";

export const offerFormSchema = z
    .object({
        id: z.number().optional(),
        contrat_pro: z.boolean(),
        apprentissage: z.boolean(),
        pro_a: z.boolean(),
        product: z.string({ required_error: "La formation est requise" }).min(1, "La formation est requise"),
        available_seats: z
            .number({
                invalid_type_error: "Vous devez indiquer le nombre de places disponibles"
            })
            .int("Indiquez un nombre entier de places")
            .min(1, "Indiquez au moins une place")
            .optional(),
        job_link: z.string().optional(),
        interview_slots_description: z.string().optional(),
        desired_starting_date: z
            .string()
            .optional()
            .transform(value => (value === "" ? undefined : value)),
        rqth: z.boolean(),
        age_max_toggle: z.boolean(),
        age_max: z
            .number()
            .nullable()
            .optional()
            .transform(value => value ?? null),
        max_commute_toggle: z.boolean(),
        max_commute: z
            .number()
            .nullable()
            .optional()
            .transform(value => value ?? null),
        active: z.boolean(),
        found_by_candidate: z.boolean(),
        found_by_csm: z.boolean(),
        candidate: z
            .object({
                first_name: z.string().optional(),
                last_name: z.string().optional(),
                email: z.string().optional(),
                phone: z.string().optional()
            })
            .optional(),
        current_job: z
            .object({
                title: z.string(),
                classification: z.string(),
                level: z.string(),
                hierarchical_coefficient: z.string(),
                starting_date: z
                    .string()
                    .regex(/(\d{2}\/\d{2}\/\d{4}|)/, "La date doit être au format DD/MM/YYYY")
                    .optional()
                    .transform(value => (value === "" ? undefined : value?.split("/").reverse().join("-")))
            })
            .optional()
    })
    .refine(values => values.contrat_pro || values.apprentissage || values.pro_a, {
        message: "Vous devez sélectionner au moins un type de contrat",
        path: ["apprentissage"]
    })
    .refine(values => !values.age_max_toggle || values.age_max, {
        message: "Vous devez indiquer l'age maximum",
        path: ["age_max"]
    })
    .refine(values => !values.max_commute_toggle || values.max_commute, {
        message: "Vous devez indiquer la durée maximal de trajet",
        path: ["max_commute"]
    })
    .refine(values => !values.pro_a || (values.candidate?.first_name && values.candidate.first_name.trim()), {
        message: "Le prénom du candidat est requis",
        path: ["candidate.first_name"]
    })
    .refine(values => !values.pro_a || (values.candidate?.last_name && values.candidate.last_name.trim()), {
        message: "Le nom du candidat est requis",
        path: ["candidate.last_name"]
    })
    .refine(values => !values.pro_a || (values.candidate?.email && values.candidate.email.trim()), {
        message: "L'email du candidat est requis",
        path: ["candidate.email"]
    })
    .refine(values => !values.pro_a || (values.candidate?.phone && phoneNumberValidator(values.candidate.phone)), {
        message: "Le numéro de téléphone n'est pas valide",
        path: ["candidate.phone"]
    });

export type OfferFormData = z.infer<typeof offerFormSchema>;

// The legacy offer dialog shares the schema and must stay valid without this field.
export const offerFormSchemaWithSeats = offerFormSchema.refine(values => values.available_seats != null, {
    message: "Vous devez indiquer le nombre de places disponibles",
    path: ["available_seats"]
});
